import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import { stagingStoreSet, stagingStoreNotify, getCurrentValueForKey } from "@/hooks/useAdminStorage";

import { EDGE_FUNCTIONS, getEdgeFunctionHeaders } from "@/utils/edgeFunctionUrl";
import SEOHead from "@/components/feature/SEOHead";

const SUBMIT_REVIEW_EDGE_FN = EDGE_FUNCTIONS.submitReview;

export const SUBMITTED_REVIEWS_KEY = "ma_submitted_reviews";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SubmittedReview {
  id: string;
  name: string;
  role: string;
  medium: string;
  rating: number;
  quote: string;
  image: string | null;
  submittedAt: string;
  visible: boolean;
  featured?: boolean;
  sectionOverride?: "makers" | "visitors";
  imagePosition?: string;
}

interface SidebarBullet {
  icon: string;
  text: string;
}

interface LeaveReviewPageContent {
  pageVisible: boolean;
  eyebrow: string;
  heading: string;
  subheading: string;
  sidebarHeading: string;
  sidebarBody: string;
  sidebarBullets: SidebarBullet[];
  sidebarLinkLabel: string;
  sidebarLinkSub: string;
  formHeading: string;
  labelName: string;
  labelEmail: string;
  labelRole: string;
  labelMedium: string;
  labelRating: string;
  labelReview: string;
  labelPhoto: string;
  labelPhotoSub: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  mediumPlaceholder: string;
  reviewPlaceholder: string;
  consentText: string;
  submitLabel: string;
  successHeading: string;
  successBody: string;
  heroImage: string;
}

const DEFAULT_CONTENT: LeaveReviewPageContent = {
  pageVisible: false,
  eyebrow: "Share Your Story",
  heading: "Leave Us a Review",
  subheading: "Whether you're an artist who calls our shop home or a customer who discovered something special — we'd love to hear about your experience.",
  sidebarHeading: "Your Voice Matters",
  sidebarBody: "Reviews help other artists decide if Makers Alley Co. is the right home for their work — and help shoppers discover what makes our community special.",
  sidebarBullets: [
    { icon: "ri-palette-line", text: "Artists & vendors welcome" },
    { icon: "ri-shopping-bag-line", text: "Customers & shoppers too" },
    { icon: "ri-image-line", text: "Add a photo of yourself or your work" },
    { icon: "ri-star-line", text: "Rate your experience 1–5 stars" },
  ],
  sidebarLinkLabel: "See What Others Are Saying",
  sidebarLinkSub: "Read reviews from our artist community",
  formHeading: "Your Review",
  labelName: "Your Name",
  labelEmail: "Email Address",
  labelRole: "I am a...",
  labelMedium: "Art Medium / Category",
  labelRating: "Your Rating",
  labelReview: "Your Review",
  labelPhoto: "Add a Photo",
  labelPhotoSub: "A photo of you, your art, or your experience at the shop. We may feature it on our testimonials page.",
  namePlaceholder: "Jane Smith",
  emailPlaceholder: "jane@example.com",
  mediumPlaceholder: "e.g. Ceramics, Jewelry, Shopper...",
  reviewPlaceholder: "Tell us about your experience — what you loved, what surprised you, what you'd tell a friend...",
  consentText: "I consent to Makers Alley Co. publishing my review and photo (if provided) on their website and social media. My email address will not be published.",
  submitLabel: "Submit My Review",
  successHeading: "Thank You!",
  successBody: "Your review has been submitted. We truly appreciate you taking the time to share your experience with us.",
  heroImage: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function loadSubmittedReviews(): SubmittedReview[] {
  return getCurrentValueForKey<SubmittedReview[]>(SUBMITTED_REVIEWS_KEY, []);
}

function saveSubmittedReviews(reviews: SubmittedReview[]) {
  try { localStorage.setItem(SUBMITTED_REVIEWS_KEY, JSON.stringify(reviews)); } catch { /* ignore */ }
  stagingStoreSet(SUBMITTED_REVIEWS_KEY, reviews);
  stagingStoreNotify(SUBMITTED_REVIEWS_KEY);
}

async function pushReviewsToSupabase(reviews: SubmittedReview[]): Promise<void> {
  try {
    const res = await fetch(EDGE_FUNCTIONS.saveSiteContent, {
      method: "POST",
      headers: {
        ...getEdgeFunctionHeaders(),
        "x-admin-token": "makersalley2024",
      },
      body: JSON.stringify({
        updates: { [SUBMITTED_REVIEWS_KEY]: reviews },
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      console.error(`[leave-a-review] save-site-content returned HTTP ${res.status}: ${text}`);
    }
  } catch (err) {
    console.error("[leave-a-review] Failed to push reviews to Supabase:", err);
  }
}

// ─── StarPicker ───────────────────────────────────────────────────────────────

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          className="text-3xl cursor-pointer transition-transform hover:scale-110"
        >
          <i className={`ri-star-fill ${(hovered || value) >= star ? "text-amber-400" : "text-gray-300"}`}></i>
        </button>
      ))}
    </div>
  );
}

// ─── Editable label helper ────────────────────────────────────────────────────

function EditableLabel({
  isEditing,
  value,
  onSave,
  className = "",
  required,
  suffix,
}: {
  isEditing: boolean;
  value: string;
  onSave: (v: string) => void;
  className?: string;
  required?: boolean;
  suffix?: React.ReactNode;
}) {
  if (isEditing) {
    return (
      <AdminEditField value={value} onSave={onSave} inputClassName={`text-sm font-semibold ${className}`}>
        <span className={`text-sm font-semibold ${className}`}>
          {value}{required && " *"}{suffix}
        </span>
      </AdminEditField>
    );
  }
  return (
    <span className={`text-sm font-semibold ${className}`}>
      {value}{required && " *"}{suffix}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LeaveAReviewPage() {
  const { isAdmin, isEditing } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const navigate = useNavigate();
  const [content, setContent] = useAdminStorage<LeaveReviewPageContent>("ma_leave_review_v3", DEFAULT_CONTENT);

  useEffect(() => {
    if (!content.pageVisible && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [content.pageVisible, isAdmin, navigate]);

  const [rating, setRating] = useState(0);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);

  const [pendingReviews, setPendingReviews] = useState<SubmittedReview[]>(() => loadSubmittedReviews());
  const [showPending, setShowPending] = useState(false);

  const set = <K extends keyof LeaveReviewPageContent>(key: K, val: LeaveReviewPageContent[K]) => {
    setContent({ ...content, [key]: val });
  };

  const updateBullet = (idx: number, field: keyof SidebarBullet, val: string) => {
    const bullets = content.sidebarBullets.map((b, i) => i === idx ? { ...b, [field]: val } : b);
    set("sidebarBullets", bullets);
  };

  const addBullet = () => {
    set("sidebarBullets", [...content.sidebarBullets, { icon: "ri-check-line", text: "New bullet point" }]);
  };

  const removeBullet = (idx: number) => {
    set("sidebarBullets", content.sidebarBullets.filter((_, i) => i !== idx));
  };

  if (!content.pageVisible && !isAdmin) {
    return null;
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea[name='review']") as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setStatus("submitting");

    const fd = new FormData(form);
    const nameVal = fd.get("name")?.toString() ?? "";
    const emailVal = fd.get("email")?.toString() ?? "";
    const roleVal = fd.get("role")?.toString() ?? "";
    const mediumVal = fd.get("medium")?.toString() ?? "";
    const reviewVal = fd.get("review")?.toString() ?? "";

    const newReview: SubmittedReview = {
      id: `review-${Date.now()}`,
      name: nameVal,
      role: roleVal,
      medium: mediumVal,
      rating,
      quote: reviewVal,
      image: photoPreview,
      submittedAt: new Date().toISOString(),
      visible: true,
    };

    const existing = loadSubmittedReviews();
    const updated = [...existing, newReview];
    saveSubmittedReviews(updated);
    setPendingReviews(updated);

    try {
      const reviewPayload = {
        name: nameVal,
        role: roleVal,
        medium: mediumVal,
        rating,
        quote: reviewVal,
        image: photoPreview,
      };

      const edgeRes = await fetch(SUBMIT_REVIEW_EDGE_FN, {
        method: "POST",
        headers: getEdgeFunctionHeaders(),
        body: JSON.stringify({ review: reviewPayload }),
      });

      if (edgeRes.ok) {
        const result = await edgeRes.json() as { review?: SubmittedReview };
        if (result.review) {
          const withServerId = updated.map((r) =>
            r.id === newReview.id ? result.review! : r
          );
          saveSubmittedReviews(withServerId);
          setPendingReviews(withServerId);
        }
      } else {
        const text = await edgeRes.text().catch(() => edgeRes.statusText);
        console.error(`[leave-a-review] Edge function returned HTTP ${edgeRes.status}: ${text}`);
      }
    } catch (err) {
      console.error("[leave-a-review] Review sync to Supabase failed:", err);
    }

    setStatus("success");
    form.reset();
    setPhotoFile(null);
    setPhotoPreview(null);
    setRating(0);
    setCharCount(0);
  };

  const toggleReviewVisibility = (id: string) => {
    const next = pendingReviews.map((r) => r.id === id ? { ...r, visible: !r.visible } : r);
    setPendingReviews(next);
    saveSubmittedReviews(next);
    pushReviewsToSupabase(next);
  };

  const deleteReview = (id: string) => {
    const next = pendingReviews.filter((r) => r.id !== id);
    setPendingReviews(next);
    saveSubmittedReviews(next);
    pushReviewsToSupabase(next);
  };

  const bg = isDark ? "bg-gray-950" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const inputCls = isDark
    ? "w-full border border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300";
  const labelCls = isDark ? "text-gray-300" : "text-gray-700";

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEOHead
        title="Leave a Review — Makers Alley Co"
        description="Share your experience at Makers Alley Co. Whether you're an artist vendor or a happy shopper, we'd love to hear your story. Leave a review and help others discover our handmade community."
        keywords="leave a review Makers Alley, artist review, handmade shop review, Medford Oregon review, maker marketplace feedback"
        canonicalPath="/leave-a-review"
        ogType="website"
        ogTitle="Leave a Review — Makers Alley Co"
        ogDescription="Share your experience at Makers Alley Co. Artists and shoppers welcome. Your story helps others discover our handmade community."
        lastModified="2026-05-10"
        author="Makers Alley Co"
        noIndex={!content.pageVisible}
      />
      <PreviewModeBar />
      <Navbar />

      {isAdmin && !content.pageVisible && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-center mt-14">
          <p className="text-yellow-700 text-sm font-semibold">
            <i className="ri-eye-off-line mr-1"></i>
            This page is completely hidden from visitors. Toggle visibility below to publish it.
          </p>
        </div>
      )}

      {/* Hero */}
      <div
        className="relative pt-20 pb-16 px-6 text-center overflow-hidden"
        style={content.heroImage ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
      >
        {content.heroImage && (
          <>
            <div className="absolute inset-0">
              {isEditing ? (
                <AdminImageUpload
                  src={content.heroImage}
                  alt="Hero background"
                  onReplace={(url) => set("heroImage", url)}
                  className="w-full h-full"
                  imgClassName="w-full h-full object-cover object-top"
                  isDark={isDark}
                />
              ) : (
                <img src={content.heroImage} alt="Hero background" className="w-full h-full object-cover object-top" />
              )}
            </div>
            <div className="absolute inset-0 bg-black/40"></div>
          </>
        )}
        <div className="relative max-w-3xl mx-auto">
          {isEditing ? (
            <AdminEditField value={content.eyebrow} onSave={(v) => set("eyebrow", v)} className="mb-2" inputClassName="text-sm font-bold uppercase tracking-widest text-center">
              <span className={`text-sm font-bold uppercase tracking-widest ${content.heroImage ? "text-green-300" : "text-green-600"}`}>{content.eyebrow}</span>
            </AdminEditField>
          ) : (
            <span className={`text-sm font-bold uppercase tracking-widest ${content.heroImage ? "text-green-300" : "text-green-600"}`}>{content.eyebrow}</span>
          )}
          {isEditing ? (
            <AdminEditField value={content.heading} onSave={(v) => set("heading", v)} className="mt-3 mb-4" inputClassName="text-5xl font-extrabold text-center">
              <h1 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${content.heroImage ? "text-white" : "text-gray-900"}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {content.heading}
              </h1>
            </AdminEditField>
          ) : (
            <h1 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${content.heroImage ? "text-white" : "text-gray-900"}`} style={{ fontFamily: headingFont, ...headingStyle }}>
              {content.heading}
            </h1>
          )}
          {isEditing ? (
            <AdminEditField value={content.subheading} onSave={(v) => set("subheading", v)} multiline inputClassName="text-lg text-center">
              <p className={`text-lg max-w-2xl mx-auto ${content.heroImage ? "text-white/90" : "text-gray-600"}`}>{content.subheading}</p>
            </AdminEditField>
          ) : (
            <p className={`text-lg max-w-2xl mx-auto ${content.heroImage ? "text-white/90" : "text-gray-600"}`}>{content.subheading}</p>
          )}
          {isEditing && !content.heroImage && (
            <button
              onClick={() => {
                const url = prompt("Paste a hero image URL (or leave blank to remove):");
                if (url !== null) set("heroImage", url);
              }}
              className="mt-4 text-xs font-semibold text-green-600 hover:text-green-800 border border-green-300 px-3 py-1.5 rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-image-add-line mr-1"></i>Add Hero Image
            </button>
          )}
          {isEditing && content.heroImage && (
            <button
              onClick={() => set("heroImage", "")}
              className="mt-4 text-xs font-semibold text-red-400 hover:text-red-600 border border-red-200 px-3 py-1.5 rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-close-circle-line mr-1"></i>Remove Hero Image
            </button>
          )}
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left — info sidebar */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div className={`rounded-3xl overflow-hidden border ${isDark ? "border-pink-800/40" : "border-transparent"}`}>
              <div className="p-8" style={{ background: isDark ? "linear-gradient(135deg, #9d174d 0%, #14532d 100%)" : "linear-gradient(135deg, #f9a8d4 0%, #86efac 100%)" }}>
                <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-5 ${isDark ? "bg-white/20" : "bg-white"}`}>
                  <i className="ri-chat-heart-line text-pink-500 text-2xl"></i>
                </div>
                {isEditing ? (
                  <AdminEditField value={content.sidebarHeading} onSave={(v) => set("sidebarHeading", v)} inputClassName="text-2xl font-extrabold text-white">
                    <h2 className="text-white font-extrabold text-2xl mb-3" style={{ fontFamily: headingFont, ...headingStyle }}>
                      {content.sidebarHeading}
                    </h2>
                  </AdminEditField>
                ) : (
                  <h2 className="text-white font-extrabold text-2xl mb-3" style={{ fontFamily: headingFont, ...headingStyle }}>
                    {content.sidebarHeading}
                  </h2>
                )}
                {isEditing ? (
                  <AdminEditField value={content.sidebarBody} onSave={(v) => set("sidebarBody", v)} multiline inputClassName="text-sm text-white/90">
                    <p className="text-white/90 text-sm leading-relaxed mb-6">{content.sidebarBody}</p>
                  </AdminEditField>
                ) : (
                  <p className="text-white/90 text-sm leading-relaxed mb-6">{content.sidebarBody}</p>
                )}
                <div className="space-y-3">
                  {content.sidebarBullets.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 group/bullet">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${isDark ? "bg-white/20" : "bg-white/30"}`}>
                        <i className={`${item.icon} text-white text-sm`}></i>
                      </div>
                      {isEditing ? (
                        <div className="flex-1 flex items-center gap-1">
                          <AdminEditField value={item.text} onSave={(v) => updateBullet(idx, "text", v)} inputClassName="text-sm text-white/90">
                            <span className="text-white/90 text-sm">{item.text}</span>
                          </AdminEditField>
                          <button
                            onClick={() => removeBullet(idx)}
                            className="opacity-0 group-hover/bullet:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center bg-red-500/70 hover:bg-red-500 text-white rounded-full cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <span className="text-white/90 text-sm">{item.text}</span>
                      )}
                    </div>
                  ))}
                  {isEditing && (
                    <button
                      onClick={addBullet}
                      className="flex items-center gap-2 text-white/70 hover:text-white text-xs font-semibold cursor-pointer transition-colors mt-2"
                    >
                      <i className="ri-add-line"></i> Add bullet
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`mt-6 rounded-2xl border p-5 ${cardBg}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
                  <i className="ri-star-fill text-amber-400 text-lg"></i>
                </div>
                <div>
                  {isEditing ? (
                    <AdminEditField value={content.sidebarLinkLabel} onSave={(v) => set("sidebarLinkLabel", v)} inputClassName="font-bold text-sm">
                      <div className={`font-bold text-sm ${headingColor}`}>{content.sidebarLinkLabel}</div>
                    </AdminEditField>
                  ) : (
                    <div className={`font-bold text-sm ${headingColor}`}>{content.sidebarLinkLabel}</div>
                  )}
                  {isEditing ? (
                    <AdminEditField value={content.sidebarLinkSub} onSave={(v) => set("sidebarLinkSub", v)} inputClassName="text-xs">
                      <div className={`text-xs ${subColor}`}>{content.sidebarLinkSub}</div>
                    </AdminEditField>
                  ) : (
                    <div className={`text-xs ${subColor}`}>{content.sidebarLinkSub}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate("/testimonials")}
                className="w-full text-sm font-semibold text-pink-500 hover:text-pink-700 flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
              >
                View All Reviews <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          {/* Right — form */}
          <div className="w-full lg:w-3/5">
            {status === "success" ? (
              <div className={`rounded-3xl border p-12 text-center ${cardBg}`}>
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                {isEditing ? (
                  <AdminEditField value={content.successHeading} onSave={(v) => set("successHeading", v)} inputClassName="text-2xl font-extrabold text-center">
                    <h2 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                      {content.successHeading}
                    </h2>
                  </AdminEditField>
                ) : (
                  <h2 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                    {content.successHeading}
                  </h2>
                )}
                {isEditing ? (
                  <AdminEditField value={content.successBody} onSave={(v) => set("successBody", v)} multiline inputClassName="text-base text-center">
                    <p className={`text-base mb-6 ${subColor}`}>{content.successBody}</p>
                  </AdminEditField>
                ) : (
                  <p className={`text-base mb-6 ${subColor}`}>{content.successBody}</p>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => setStatus("idle")}
                    className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                  >
                    Submit Another Review
                  </button>
                  <button
                    onClick={() => navigate("/testimonials")}
                    className={`border font-semibold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300 hover:border-gray-400" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                  >
                    See All Reviews
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                id="leave-a-review-form"
                className={`rounded-3xl border p-8 space-y-6 ${cardBg}`}
              >
                {isEditing ? (
                  <AdminEditField value={content.formHeading} onSave={(v) => set("formHeading", v)} inputClassName="text-xl font-extrabold">
                    <h2 className={`text-xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                      {content.formHeading}
                    </h2>
                  </AdminEditField>
                ) : (
                  <h2 className={`text-xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                    {content.formHeading}
                  </h2>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block mb-1.5 ${labelCls}`}>
                      <EditableLabel isEditing={isEditing} value={content.labelName} onSave={(v) => set("labelName", v)} className={labelCls} required />
                    </label>
                    <input type="text" name="name" required placeholder={content.namePlaceholder} className={inputCls} />
                    {isEditing && (
                      <AdminEditField value={content.namePlaceholder} onSave={(v) => set("namePlaceholder", v)} inputClassName="text-xs">
                        <span className={`text-xs ${subColor}`}>Placeholder: {content.namePlaceholder}</span>
                      </AdminEditField>
                    )}
                  </div>
                  <div>
                    <label className={`block mb-1.5 ${labelCls}`}>
                      <EditableLabel isEditing={isEditing} value={content.labelEmail} onSave={(v) => set("labelEmail", v)} className={labelCls} required />
                    </label>
                    <input type="email" name="email" required placeholder={content.emailPlaceholder} className={inputCls} />
                    {isEditing ? (
                      <AdminEditField value={content.emailPlaceholder} onSave={(v) => set("emailPlaceholder", v)} inputClassName="text-xs">
                        <p className={`text-xs mt-1 ${subColor}`}>Placeholder: {content.emailPlaceholder}</p>
                      </AdminEditField>
                    ) : (
                      <p className={`text-xs mt-1 ${subColor}`}>Not published publicly</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block mb-1.5 ${labelCls}`}>
                      <EditableLabel isEditing={isEditing} value={content.labelRole} onSave={(v) => set("labelRole", v)} className={labelCls} required />
                    </label>
                    <select name="role" required className={`${inputCls} cursor-pointer`}>
                      <option value="">Select your role</option>
                      <option value="Artist Vendor">Artist / Vendor</option>
                      <option value="Customer">Customer / Shopper</option>
                      <option value="Visitor">Visitor</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block mb-1.5 ${labelCls}`}>
                      <EditableLabel isEditing={isEditing} value={content.labelMedium} onSave={(v) => set("labelMedium", v)} className={labelCls} />
                    </label>
                    <input type="text" name="medium" placeholder={content.mediumPlaceholder} className={inputCls} />
                    {isEditing && (
                      <AdminEditField value={content.mediumPlaceholder} onSave={(v) => set("mediumPlaceholder", v)} inputClassName="text-xs">
                        <span className={`text-xs ${subColor}`}>Placeholder: {content.mediumPlaceholder}</span>
                      </AdminEditField>
                    )}
                  </div>
                </div>

                <div>
                  <label className={`block mb-2 ${labelCls}`}>
                    <EditableLabel isEditing={isEditing} value={content.labelRating} onSave={(v) => set("labelRating", v)} className={labelCls} required />
                  </label>
                  <StarPicker value={rating} onChange={setRating} />
                  <input type="hidden" name="rating" value={rating} />
                  <p className={`text-xs mt-1.5 ${subColor}`}>
                    {rating === 0 ? "Select a rating" : rating === 5 ? "Excellent!" : rating === 4 ? "Great!" : rating === 3 ? "Good" : rating === 2 ? "Fair" : "Poor"}
                  </p>
                </div>

                <div>
                  <label className={`block mb-1.5 ${labelCls}`}>
                    <EditableLabel
                      isEditing={isEditing}
                      value={content.labelReview}
                      onSave={(v) => set("labelReview", v)}
                      className={labelCls}
                      required
                      suffix={<span className={`font-normal ml-1 ${charCount > 500 ? "text-red-500" : subColor}`}>({charCount}/500)</span>}
                    />
                  </label>
                  <textarea
                    name="review"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder={content.reviewPlaceholder}
                    className={`${inputCls} resize-none`}
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                  {isEditing && (
                    <AdminEditField value={content.reviewPlaceholder} onSave={(v) => set("reviewPlaceholder", v)} multiline inputClassName="text-xs">
                      <span className={`text-xs ${subColor}`}>Placeholder: {content.reviewPlaceholder}</span>
                    </AdminEditField>
                  )}
                </div>

                <div>
                  <label className={`block mb-1.5 ${labelCls}`}>
                    <EditableLabel
                      isEditing={isEditing}
                      value={content.labelPhoto}
                      onSave={(v) => set("labelPhoto", v)}
                      className={labelCls}
                      suffix={<span className={`font-normal ml-1 ${subColor}`}>(optional)</span>}
                    />
                  </label>
                  <div className={`border-2 border-dashed rounded-xl transition-colors ${
                    photoPreview
                      ? "border-green-300"
                      : isDark ? "border-gray-600 hover:border-pink-400" : "border-pink-200 hover:border-pink-400"
                  }`}>
                    <input
                      type="file"
                      name="photo"
                      accept="image/*"
                      id="review-photo"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="review-photo" className="cursor-pointer block">
                      {photoPreview ? (
                        <div className="relative">
                          <img src={photoPreview} alt="Preview" className="w-full h-40 object-cover rounded-xl" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl opacity-0 hover:opacity-100 transition-opacity">
                            <span className="text-white text-xs font-bold bg-black/50 px-3 py-1.5 rounded-full">Change Photo</span>
                          </div>
                        </div>
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <i className={`ri-image-add-line text-3xl mb-2 block ${isDark ? "text-gray-500" : "text-pink-300"}`}></i>
                          <p className={`text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            Click to upload a photo of yourself or your work
                          </p>
                          <p className={`text-xs mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}>JPG, PNG — max 10MB</p>
                        </div>
                      )}
                    </label>
                  </div>
                  {isEditing ? (
                    <AdminEditField value={content.labelPhotoSub} onSave={(v) => set("labelPhotoSub", v)} multiline inputClassName="text-xs">
                      <p className={`text-xs mt-1.5 ${subColor}`}>{content.labelPhotoSub}</p>
                    </AdminEditField>
                  ) : (
                    <p className={`text-xs mt-1.5 ${subColor}`}>{content.labelPhotoSub}</p>
                  )}
                </div>

                <div className={`border rounded-xl p-4 ${isDark ? "bg-gray-700 border-gray-600" : "bg-pink-50 border-pink-200"}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      required
                      className="mt-0.5 w-4 h-4 accent-pink-500 flex-shrink-0 cursor-pointer"
                    />
                    {isEditing ? (
                      <AdminEditField value={content.consentText} onSave={(v) => set("consentText", v)} multiline inputClassName="text-sm">
                        <span className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {content.consentText}
                        </span>
                      </AdminEditField>
                    ) : (
                      <span className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {content.consentText}
                      </span>
                    )}
                  </label>
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  {status === "submitting" ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Submitting...</>
                  ) : (
                    <>
                      <i className="ri-star-line"></i>
                      {isEditing ? (
                        <AdminEditField value={content.submitLabel} onSave={(v) => set("submitLabel", v)} inputClassName="text-base font-bold text-white">
                          <span>{content.submitLabel}</span>
                        </AdminEditField>
                      ) : (
                        <span>{content.submitLabel}</span>
                      )}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Admin: Submitted Reviews Panel */}
        {isAdmin && (
          <div className={`mt-16 rounded-3xl border overflow-hidden ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <button
              onClick={() => setShowPending(!showPending)}
              className={`w-full flex items-center justify-between px-6 py-5 cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-pink-100 flex-shrink-0">
                  <i className="ri-inbox-line text-pink-500 text-base"></i>
                </div>
                <div className="text-left">
                  <div className={`font-bold text-sm ${headingColor}`}>
                    Submitted Reviews ({pendingReviews.length})
                  </div>
                  <div className={`text-xs ${subColor}`}>
                    {pendingReviews.filter((r) => r.visible).length} visible · {pendingReviews.filter((r) => !r.visible).length} hidden
                  </div>
                </div>
              </div>
              <i className={`${showPending ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400`}></i>
            </button>

            {showPending && (
              <div className={`border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
                {pendingReviews.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <p className={`text-sm ${subColor}`}>No reviews submitted yet. They'll appear here when visitors submit the form.</p>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
                    {pendingReviews.map((review) => (
                      <div key={review.id} className={`px-6 py-5 flex gap-4 items-start ${isDark ? "hover:bg-gray-700/30" : "hover:bg-gray-50"}`}>
                        <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                          {review.image ? (
                            <img src={review.image} alt={review.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                              <i className={`ri-user-line text-lg ${subColor}`}></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`font-bold text-sm ${headingColor}`}>{review.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>{review.role}</span>
                            {review.medium && <span className={`text-xs ${subColor}`}>· {review.medium}</span>}
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map((s) => (
                                <i key={s} className={`ri-star-fill text-xs ${s <= review.rating ? "text-amber-400" : "text-gray-300"}`}></i>
                              ))}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${review.visible ? (isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600") : (isDark ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400")}`}>
                              {review.visible ? "Visible" : "Hidden"}
                            </span>
                          </div>
                          <p className={`text-sm italic leading-relaxed mb-2 ${subColor}`}>"{review.quote}"</p>
                          <p className={`text-xs ${subColor}`}>{new Date(review.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <button
                            onClick={() => toggleReviewVisibility(review.id)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                              review.visible
                                ? isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                : "bg-green-500 hover:bg-green-600 text-white"
                            }`}
                          >
                            <i className={`${review.visible ? "ri-eye-off-line" : "ri-eye-line"} mr-1`}></i>
                            {review.visible ? "Hide" : "Show"}
                          </button>
                          <button
                            onClick={() => deleteReview(review.id)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className={`px-6 py-4 border-t ${isDark ? "border-gray-700 bg-gray-700/30" : "border-gray-100 bg-gray-50"}`}>
                  <p className={`text-xs ${subColor}`}>
                    <i className="ri-information-line mr-1"></i>
                    Visible reviews automatically appear on the{" "}
                    <button onClick={() => navigate("/testimonials")} className="text-pink-500 hover:text-pink-700 cursor-pointer font-semibold">
                      Testimonials page
                    </button>. Hidden reviews are invisible to visitors.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {isAdmin && (
        <div className={`w-full border-t px-6 py-5 ${isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"}`}>
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className={`font-semibold text-sm flex items-center gap-2 ${headingColor}`}>
                <i className="ri-shield-check-line text-green-400"></i>
                Admin — Page Visibility
              </p>
              <p className={`text-xs mt-0.5 ${subColor}`}>When hidden, this page is completely invisible — no "coming soon", no redirect. It simply doesn't exist to visitors.</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <span className={`text-sm ${subColor}`}>Page visible to public:</span>
              <button
                type="button"
                onClick={() => set("pageVisible", !content.pageVisible)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${content.pageVisible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.pageVisible ? "translate-x-7" : "translate-x-1"}`}></span>
              </button>
              <span className={`font-bold text-sm ${content.pageVisible ? "text-green-500" : subColor}`}>
                {content.pageVisible ? "Live" : "Hidden"}
              </span>
            </label>
          </div>
        </div>
      )}

      <Footer />
      <AdminSaveBar />
    </div>
  );
}